import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {

    Plus,
    Trash2,
    Save,
    Tag,
    CreditCard,
    Store,
    BarChart,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";

// Zod Schemas for Settings
const generalSettingsSchema = z.object({
    shop_name: z.string().min(1, "Shop Name is required"),
    support_email: z.string().email().optional().or(z.literal("")),
    support_phone: z.string().optional(),
    address: z.string().optional(),
});

const paymentSettingsSchema = z.object({
    bkash_number: z.string().optional(),
    nagad_number: z.string().optional(),
    cod_enabled: z.boolean().default(true),
});

const marketingSettingsSchema = z.object({
    facebook_pixel_id: z.string().optional(),
    google_analytics_id: z.string().optional(),
});

// Coupon Schema
const couponSchema = z.object({
    code: z.string().min(3).regex(/^[A-Za-z0-9_-]+$/),
    description: z.string().optional(),
    type: z.enum(["percent", "fixed"]),
    value: z.coerce.number().positive(),
    minOrderAmount: z.coerce.number().min(0).optional(),
    expiryDate: z.string().optional(),
    usageLimit: z.coerce.number().int().positive().optional(),
    isActive: z.boolean().default(true),
});

type Coupon = z.infer<typeof couponSchema> & { id?: string; createdAt?: string };

export function SettingsPage() {


    // Forms
    const generalForm = useForm({ resolver: zodResolver(generalSettingsSchema) });
    const paymentForm = useForm({ resolver: zodResolver(paymentSettingsSchema) });
    const marketingForm = useForm({ resolver: zodResolver(marketingSettingsSchema) });

    // Coupon State
    const [coupons, setCoupons] = useState<Coupon[]>([]);
    const [couponFormOpen, setCouponFormOpen] = useState(false);
    const couponForm = useForm({
        resolver: zodResolver(couponSchema),
        defaultValues: {
            code: "",
            type: "fixed",
            value: 0,
            isActive: true,
        }
    });

    // Fetch All Data
    useEffect(() => {
        const fetchSettings = async () => {

            try {
                // Fetch Settings
                const [settingsRes, couponsRes] = await Promise.all([
                    fetch("/api/admin/settings"),
                    fetch("/api/admin/coupons")
                ]);

                if (settingsRes.ok) {
                    const data = await settingsRes.json();
                    const s = data.data;
                    if (s.shop_info) generalForm.reset(s.shop_info);
                    if (s.payment_config) paymentForm.reset(s.payment_config);
                    if (s.analytics) marketingForm.reset(s.analytics);
                }

                if (couponsRes.ok) {
                    const data = await couponsRes.json();
                    setCoupons(data.data || []);
                }

            } catch (error) {
                console.error("Failed to fetch settings", error);
            } finally {

            }
        };

        fetchSettings();
    }, []);

    // Save Handlers
    const saveGeneral = async (data: any) => {
        try {
            await fetch("/api/admin/settings", {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ key: "shop_info", value: data }),
            });
            // Show toast success
        } catch (e) {
            console.error(e);
        }
    };

    const savePayment = async (data: any) => {
        try {
            await fetch("/api/admin/settings", {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ key: "payment_config", value: data }),
            });
        } catch (e) { console.error(e); }
    };

    const saveMarketing = async (data: any) => {
        try {
            await fetch("/api/admin/settings", {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ key: "analytics", value: data }),
            });
        } catch (e) { console.error(e); }
    };

    // Coupon Handlers
    const createCoupon = async (data: any) => {
        try {
            const res = await fetch("/api/admin/coupons", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(data),
            });
            if (res.ok) {
                const newCoupon = (await res.json()).data;
                setCoupons([newCoupon, ...coupons]);
                setCouponFormOpen(false);
                couponForm.reset();
            }
        } catch (e) { console.error(e); }
    };

    const deleteCoupon = async (code: string) => {
        if (!confirm("Delete this coupon?")) return;
        try {
            const res = await fetch(`/api/admin/coupons/${code}`, {
                method: "DELETE"
            });
            if (res.ok) {
                setCoupons(coupons.filter(c => c.code !== code));
            }
        } catch (e) { console.error(e); }
    };

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
                <p className="text-muted-foreground">Manage your store settings and coupons.</p>
            </div>

            <Tabs defaultValue="general" className="w-full">
                <TabsList className="grid w-full grid-cols-4 lg:w-[400px]">
                    <TabsTrigger value="general">General</TabsTrigger>
                    <TabsTrigger value="payment">Payment</TabsTrigger>
                    <TabsTrigger value="marketing">Marketing</TabsTrigger>
                    <TabsTrigger value="coupons">Coupons</TabsTrigger>
                </TabsList>

                {/* General Settings */}
                <TabsContent value="general">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2"><Store className="h-5 w-5" /> Shop Information</CardTitle>
                            <CardDescription>Configure your store name and contact details.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <Form {...generalForm}>
                                <form onSubmit={generalForm.handleSubmit(saveGeneral)} className="space-y-4">
                                    <FormField control={generalForm.control} name="shop_name" render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Shop Name</FormLabel>
                                            <FormControl><Input placeholder="AutoEcom Store" {...field} /></FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )} />
                                    <div className="grid gap-4 sm:grid-cols-2">
                                        <FormField control={generalForm.control} name="support_email" render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Support Email</FormLabel>
                                                <FormControl><Input placeholder="support@example.com" {...field} /></FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )} />
                                        <FormField control={generalForm.control} name="support_phone" render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Support Phone</FormLabel>
                                                <FormControl><Input placeholder="+880..." {...field} /></FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )} />
                                    </div>
                                    <FormField control={generalForm.control} name="address" render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Address</FormLabel>
                                            <FormControl><Input placeholder="Dhaka, Bangladesh" {...field} /></FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )} />
                                    <Button type="submit" disabled={generalForm.formState.isSubmitting}>
                                        <Save className="mr-2 h-4 w-4" /> Save General Settings
                                    </Button>
                                </form>
                            </Form>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Payment Settings */}
                <TabsContent value="payment">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2"><CreditCard className="h-5 w-5" /> Payment Configuration</CardTitle>
                            <CardDescription>Manage payment methods and numbers.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <Form {...paymentForm}>
                                <form onSubmit={paymentForm.handleSubmit(savePayment)} className="space-y-4">
                                    <div className="grid gap-4 sm:grid-cols-2">
                                        <FormField control={paymentForm.control} name="bkash_number" render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>bKash Number</FormLabel>
                                                <FormControl><Input placeholder="017..." {...field} /></FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )} />
                                        <FormField control={paymentForm.control} name="nagad_number" render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Nagad Number</FormLabel>
                                                <FormControl><Input placeholder="016..." {...field} /></FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )} />
                                    </div>
                                    <FormField control={paymentForm.control} name="cod_enabled" render={({ field }) => (
                                        <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                                            <FormControl>
                                                <Checkbox
                                                    checked={field.value}
                                                    onCheckedChange={field.onChange}
                                                />
                                            </FormControl>
                                            <div className="space-y-1 leading-none">
                                                <FormLabel>
                                                    Enable Cash on Delivery
                                                </FormLabel>
                                                <FormDescription>
                                                    Allow customers to pay when they receive the product.
                                                </FormDescription>
                                            </div>
                                        </FormItem>
                                    )} />
                                    <Button type="submit" disabled={paymentForm.formState.isSubmitting}>
                                        <Save className="mr-2 h-4 w-4" /> Save Payment Settings
                                    </Button>
                                </form>
                            </Form>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Marketing Settings */}
                <TabsContent value="marketing">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2"><BarChart className="h-5 w-5" /> Marketing & Analytics</CardTitle>
                            <CardDescription>Integrate with 3rd party analytics tools.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <Form {...marketingForm}>
                                <form onSubmit={marketingForm.handleSubmit(saveMarketing)} className="space-y-4">
                                    <FormField control={marketingForm.control} name="facebook_pixel_id" render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Facebook Pixel ID</FormLabel>
                                            <FormControl><Input placeholder="Pixel ID" {...field} /></FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )} />
                                    <FormField control={marketingForm.control} name="google_analytics_id" render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Google Analytics ID</FormLabel>
                                            <FormControl><Input placeholder="G-..." {...field} /></FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )} />
                                    <Button type="submit" disabled={marketingForm.formState.isSubmitting}>
                                        <Save className="mr-2 h-4 w-4" /> Save Marketing Settings
                                    </Button>
                                </form>
                            </Form>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Coupons */}
                <TabsContent value="coupons">
                    <Card>
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle className="flex items-center gap-2"><Tag className="h-5 w-5" /> Coupons</CardTitle>
                                    <CardDescription>Create and manage discount codes.</CardDescription>
                                </div>
                                <Dialog open={couponFormOpen} onOpenChange={setCouponFormOpen}>
                                    <DialogTrigger asChild>
                                        <Button><Plus className="mr-2 h-4 w-4" /> Create Coupon</Button>
                                    </DialogTrigger>
                                    <DialogContent>
                                        <DialogHeader>
                                            <DialogTitle>Create Coupon</DialogTitle>
                                            <DialogDescription>Add a new discount code.</DialogDescription>
                                        </DialogHeader>
                                        <Form {...couponForm}>
                                            <form onSubmit={couponForm.handleSubmit(createCoupon)} className="space-y-4">
                                                <FormField control={couponForm.control} name="code" render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Code</FormLabel>
                                                        <FormControl><Input placeholder="SUMMER20" {...field} className="uppercase" onChange={e => field.onChange(e.target.value.toUpperCase())} /></FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )} />
                                                <div className="grid grid-cols-2 gap-4">
                                                    <FormField control={couponForm.control} name="type" render={({ field }) => (
                                                        <FormItem>
                                                            <FormLabel>Type</FormLabel>
                                                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                                                                <FormControl>
                                                                    <SelectTrigger>
                                                                        <SelectValue placeholder="Select type" />
                                                                    </SelectTrigger>
                                                                </FormControl>
                                                                <SelectContent>
                                                                    <SelectItem value="fixed">Fixed Amount</SelectItem>
                                                                    <SelectItem value="percent">Percentage</SelectItem>
                                                                </SelectContent>
                                                            </Select>
                                                            <FormMessage />
                                                        </FormItem>
                                                    )} />
                                                    <FormField control={couponForm.control} name="value" render={({ field }) => (
                                                        <FormItem>
                                                            <FormLabel>Value</FormLabel>
                                                            <FormControl><Input type="number" {...field} /></FormControl>
                                                            <FormMessage />
                                                        </FormItem>
                                                    )} />
                                                </div>
                                                <div className="grid grid-cols-2 gap-4">
                                                    <FormField control={couponForm.control} name="minOrderAmount" render={({ field }) => (
                                                        <FormItem>
                                                            <FormLabel>Min Order</FormLabel>
                                                            <FormControl><Input type="number" placeholder="0" {...field} /></FormControl>
                                                            <FormMessage />
                                                        </FormItem>
                                                    )} />
                                                    <FormField control={couponForm.control} name="expiryDate" render={({ field }) => (
                                                        <FormItem>
                                                            <FormLabel>Expiry Date</FormLabel>
                                                            <FormControl><Input type="date" {...field} /></FormControl>
                                                            <FormMessage />
                                                        </FormItem>
                                                    )} />
                                                </div>
                                                <DialogFooter>
                                                    <Button type="submit" disabled={couponForm.formState.isSubmitting}>create</Button>
                                                </DialogFooter>
                                            </form>
                                        </Form>
                                    </DialogContent>
                                </Dialog>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Code</TableHead>
                                        <TableHead>Discount</TableHead>
                                        <TableHead>Expiry</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {coupons.map((coupon) => (
                                        <TableRow key={coupon.code}>
                                            <TableCell className="font-medium font-mono">{coupon.code}</TableCell>
                                            <TableCell>
                                                {coupon.type === 'percent' ? `${coupon.value}%` : `$${coupon.value}`}
                                            </TableCell>
                                            <TableCell>
                                                {coupon.expiryDate ? new Date(coupon.expiryDate).toLocaleDateString() : 'No Expiry'}
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant={coupon.isActive ? 'success' : 'secondary'}>
                                                    {coupon.isActive ? 'Active' : 'Inactive'}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <Button variant="ghost" size="icon" onClick={() => deleteCoupon(coupon.code)} className="text-destructive">
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {coupons.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={5} className="text-center h-24 text-muted-foreground p-4">
                                                No coupons found.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
}
